import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { UserRole } from '@/types/entities'
import { WeeklyProgrammePanel } from './WeeklyProgrammePanel'
import { FilmSocialPanel } from './FilmSocialPanel'
import { Link, useSearchParams } from 'react-router-dom'
import { CreateFilmWizard } from './CreateFilmWizard'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  ArtworkPreview,
  humanize,
  Recipe,
  StudioKpi,
  StudioStatus,
  studioButton,
  Template,
} from './StudioComponents'

type Job = {
  id: string
  template_id: string
  revision: number
  status: string
  created_at?: string
  input_registry_sha256?: string
  recipe_sha256: string
  recipe: Recipe
  progress?: string
  creationStage?: string
  creationError?: string
  error?: string
  dispatch_error?: string
  result?: { costs?: unknown; artifacts?: { role: string }[] }
  reviews?: unknown[]
}
type Library = {
  jobs: Job[]
  total: number
  offset: number
  limit: number
  summary: Record<string, number>
}
type Schedule = {
  id: string
  template_id: string
  enabled: boolean
  interval_hours: number
  next_run_at: string
}
const base = '/api/v1/films'

export function FilmStudioPage({
  embedded = false,
  embeddedView,
}: { embedded?: boolean; embeddedView?: 'library' | 'schedules' } = {}) {
  const user = useAuthStore((s) => s.user)
  const manager = user?.role === UserRole.MANAGER
  const qc = useQueryClient()
  const [params, setRouteParams] = useSearchParams()
  function setParams(next: Record<string, string>) {
    const views: Record<string, string> = {
      library: 'films',
      candidates: 'ideas',
      schedules: 'schedule',
      accounts: 'accounts',
    }
    setRouteParams(embedded ? { ...next, view: views[next.view] || next.view } : next)
  }
  const jobParam = params.get('job') || ''
  const viewParam =
    params.has('grant') || params.has('social') ? 'accounts' : params.get('view') || 'library'
  const view =
    embeddedView ||
    (['library', 'candidates', 'schedules', 'accounts'].includes(viewParam) ? viewParam : 'library')
  const [wizard, setWizard] = useState(false)
  const renderKey = useRef('')
  const detailPanel = useRef<HTMLDivElement>(null)
  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [status, setStatus] = useState('')
  const [format, setFormat] = useState('')
  const [offset, setOffset] = useState(0)
  const [templateId, setTemplateId] = useState('')
  const [selected, setSelected] = useState('')
  const [recipeText, setRecipeText] = useState('')
  const [video, setVideo] = useState('')
  const [download, setDownload] = useState<{ url: string; role: string } | null>(null)
  const [finding, setFinding] = useState('')
  const [timestamp, setTimestamp] = useState(0)
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [firstRun, setFirstRun] = useState('')
  const [interval, setInterval] = useState(24)
  const [enableSchedule, setEnableSchedule] = useState(false)
  const [reviewOpen, setReviewOpen] = useState(false)
  const [watched, setWatched] = useState(false)
  useEffect(() => {
    if (/^[a-f0-9-]{36}$/.test(jobParam)) setSelected(jobParam)
  }, [jobParam])
  useEffect(() => {
    if (selected) detailPanel.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }, [selected])
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(search.trim())
      setOffset(0)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])
  const catalog = useQuery({
    queryKey: ['film-catalog'],
    queryFn: async () => (await apiClient.get<{ templates: Template[] }>(`${base}/catalog`)).data,
    enabled: manager,
    refetchInterval: 480000,
  })
  const jobs = useQuery({
    queryKey: ['film-jobs', searchTerm, status, format, offset],
    queryFn: async () =>
      (
        await apiClient.get<Library>(`${base}/jobs`, {
          params: { q: searchTerm, status, format, offset },
        })
      ).data,
    refetchInterval: 10000,
    enabled: manager,
    placeholderData: (previous) => previous,
  })
  const detail = useQuery({
    queryKey: ['film-job', selected],
    queryFn: async () => (await apiClient.get<Job>(`${base}/jobs/${selected}`)).data,
    refetchInterval: 10000,
    enabled: manager && !!selected,
  })
  const schedules = useQuery({
    queryKey: ['film-schedules'],
    queryFn: async () =>
      (await apiClient.get<{ schedules: Schedule[] }>(`${base}/schedules`)).data.schedules,
    enabled: manager,
  })
  const template = catalog.data?.templates.find((t) => t.id === templateId)
  const job = detail.data
  useEffect(() => {
    setVideo('')
    setDownload(null)
    setFinding('')
    setTimestamp(0)
    setReviewOpen(false)
    setWatched(false)
  }, [selected])
  useEffect(() => {
    if (job) setRecipeText(JSON.stringify(job.recipe, null, 2))
  }, [job?.id]) // immutable recipe per revision
  async function perform(action: () => Promise<void>) {
    setBusy(true)
    setMessage('')
    try {
      await action()
      await qc.invalidateQueries({ queryKey: ['film-jobs'] })
      await qc.invalidateQueries({ queryKey: ['film-job'] })
      await qc.invalidateQueries({ queryKey: ['film-schedules'] })
      await qc.invalidateQueries({ queryKey: ['content-studio'] })
      return true
    } catch (e) {
      const error = e as { response?: { data?: { error?: string } }; message?: string }
      setMessage(error.response?.data?.error || error.message || 'Action failed')
      return false
    } finally {
      setBusy(false)
    }
  }
  async function submit(revision = false) {
    const recipe = revision ? JSON.parse(recipeText) : template?.recipe
    const { data } = await apiClient.post<Job>(
      `${base}/jobs`,
      {
        templateId: revision ? job?.template_id : templateId,
        recipe,
        parentJobId: revision ? job?.id : undefined,
      },
      { headers: { 'Idempotency-Key': revision ? crypto.randomUUID() : renderKey.current } }
    )
    selectJob(data.id)
    setMessage('Queued for hosted rendering; approval will still be required.')
  }
  function selectJob(id: string) {
    setSelected(id)
    setParams({ view: 'library', job: id })
  }
  async function asset(role: string, preview = false) {
    const { data } = await apiClient.get<{ url: string }>(
      `${base}/jobs/${selected}/assets/${encodeURIComponent(role)}${preview ? '' : '?download=1'}`
    )
    if (preview) setVideo(data.url)
    else {
      setDownload({ url: data.url, role })
      setMessage('Download ready below. Tap Save file; this private link expires in 10 minutes.')
    }
  }
  if (!manager)
    return (
      <main className="mx-auto max-w-4xl space-y-4 p-4">
        <h1 className="text-3xl font-semibold">Film Studio · Instagram</h1>
        <FilmSocialPanel />
      </main>
    )
  const button = studioButton
  const summary = jobs.data?.summary
  const Container = embedded ? 'section' : 'main'
  return (
    <Container className={embedded ? 'space-y-6' : 'mx-auto max-w-7xl space-y-6 p-4 md:p-8'}>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          {!embedded && (
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
              Arthaus · Creative production
            </p>
          )}
          {embedded ? (
            <h2 className="text-xl font-semibold">
              {view === 'schedules' ? 'Existing-world render schedules' : 'Films & review'}
            </h2>
          ) : (
            <h1 className="mt-1 text-3xl font-semibold">Film Studio</h1>
          )}
          <p className="mt-2 text-sm text-muted-foreground">
            Browse, render and review vertical artwork films. Private drafts first; publishing is
            separate.
          </p>
          {!embedded && (
            <Link className="mt-2 inline-block text-sm underline" to="/app/worlds">
              Build and screen scenes in World Studio →
            </Link>
          )}
        </div>
        <button
          className={`${button} bg-primary text-primary-foreground hover:bg-primary/90`}
          disabled={catalog.isLoading || !catalog.data?.templates.length || busy}
          onClick={() => {
            renderKey.current = crypto.randomUUID()
            setMessage('')
            setWizard(true)
          }}
        >
          {embedded ? 'Render existing world' : '+ Create film'}
        </button>
      </header>
      {!embedded && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5" aria-label="Studio totals">
            <StudioKpi label="Films & revisions" value={summary?.total} note="All time" />
            <StudioKpi
              label="In production"
              value={summary ? summary.queued + summary.running : undefined}
              note="Queued + rendering"
            />
            <StudioKpi
              label="Needs review"
              value={summary ? summary.review_pending + summary.revision_requested : undefined}
              note="Review + corrections"
            />
            <StudioKpi
              label="Approved films"
              value={summary?.approved}
              note="Not necessarily posted"
            />
            <StudioKpi
              label="Staged artworks"
              value={summary?.candidates}
              note="Awaiting creation review"
            />
          </div>
          <nav aria-label="Film Studio sections" className="flex flex-wrap gap-2 border-b pb-3">
            {[
              ['library', 'Film library'],
              ['candidates', 'Candidate planning'],
              ['schedules', 'Schedules'],
              ['accounts', 'Instagram accounts'],
            ].map(([id, label]) => (
              <button
                key={id}
                className={`${button} ${view === id ? 'bg-muted' : ''}`}
                aria-current={view === id ? 'page' : undefined}
                onClick={() => setParams({ view: id, ...(selected ? { job: selected } : {}) })}
              >
                {label}
              </button>
            ))}
          </nav>
        </>
      )}
      {(message || catalog.error || jobs.error || detail.error || schedules.error) && (
        <p role="status" className="rounded-md border p-3">
          {message || 'Film service unavailable. Please retry or check deployment configuration.'}
        </p>
      )}
      {view === 'candidates' && <WeeklyProgrammePanel initiallyOpen />}
      {view === 'accounts' && <FilmSocialPanel />}
      <CreateFilmWizard
        open={wizard}
        onOpenChange={setWizard}
        templates={catalog.data?.templates || []}
        templateId={templateId}
        onSelect={(id) => {
          if (id !== templateId) renderKey.current = crypto.randomUUID()
          setTemplateId(id)
          setMessage('')
        }}
        busy={busy}
        onSubmit={() => perform(() => submit())}
        error={message}
      />
      {view === 'library' && (
        <>
          <section className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <label className="w-full min-w-0 text-sm sm:w-auto sm:flex-1">
                Search films
                <input
                  aria-label="Search films"
                  type="search"
                  maxLength={120}
                  className="mt-1 min-h-11 w-full rounded-lg border bg-background px-3"
                  placeholder="Artwork, artist or job ID…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </label>
              <label className="text-sm">
                Status
                <select
                  aria-label="Film status"
                  className="mt-1 block min-h-11 max-w-full rounded-lg border bg-background px-3"
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value)
                    setOffset(0)
                  }}
                >
                  <option value="">All statuses</option>
                  {[
                    'queued',
                    'running',
                    'review_pending',
                    'approved',
                    'revision_requested',
                    'failed',
                    'cancelled',
                  ].map((s) => (
                    <option key={s} value={s}>
                      {humanize(s)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                Format
                <select
                  aria-label="Film format"
                  className="mt-1 block min-h-11 rounded-lg border bg-background px-3"
                  value={format}
                  onChange={(e) => {
                    setFormat(e.target.value)
                    setOffset(0)
                  }}
                >
                  <option value="">All formats</option>
                  {['daily', 'residency', 'loop'].map((f) => (
                    <option key={f} value={f}>
                      {humanize(f)}
                    </option>
                  ))}
                </select>
              </label>
              <button
                className={button}
                onClick={() => {
                  setSearch('')
                  setSearchTerm('')
                  setStatus('')
                  setFormat('')
                  setOffset(0)
                }}
              >
                Clear filters
              </button>
            </div>
            <div className="flex flex-wrap justify-between gap-2 text-sm text-muted-foreground">
              <h2 className="font-medium text-foreground">Film library</h2>
              <p aria-live="polite">
                {jobs.isLoading || jobs.isPlaceholderData
                  ? 'Loading matching films…'
                  : `${jobs.data?.total ?? 0} matching generations`}
              </p>
            </div>
            {!jobs.isLoading && !jobs.error && !jobs.data?.jobs.length && (
              <div className="rounded-xl border border-dashed p-8 text-center">
                <h3 className="font-medium">
                  {searchTerm || status || format
                    ? 'No films match these filters'
                    : 'Your film library is ready for its first draft'}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  {searchTerm || status || format
                    ? 'Try another artwork, artist, status or format.'
                    : 'Create a film from an existing world, or review staged artworks in Candidate planning.'}
                </p>
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {jobs.data?.jobs.map((j) => (
                <button
                  key={j.id}
                  className={`min-w-0 space-y-3 rounded-xl border bg-card p-3 text-left transition hover:shadow-md focus-visible:ring-2 focus-visible:ring-ring ${selected === j.id ? 'border-primary ring-1 ring-primary' : ''}`}
                  onClick={() => selectJob(j.id)}
                >
                  <ArtworkPreview title={j.recipe.title} jobId={j.id} />
                  <div className="flex flex-wrap justify-between gap-2">
                    <StudioStatus status={j.status} />
                    <span className="text-xs text-muted-foreground">Revision {j.revision}</span>
                  </div>
                  <strong className="block">{j.recipe.title}</strong>
                  <p className="text-xs text-muted-foreground">
                    {j.recipe.artist || 'Artist not recorded'} · {j.recipe.durationSec}s
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {j.created_at && new Date(j.created_at).toLocaleString()}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">{j.id}</p>
                </button>
              ))}
            </div>
            {!!jobs.data?.total && (
              <div className="flex items-center justify-between gap-2">
                <button
                  className={button}
                  disabled={!offset || jobs.isFetching}
                  onClick={() => setOffset(Math.max(0, offset - 24))}
                >
                  Previous
                </button>
                <span className="text-xs text-muted-foreground">
                  {offset + 1}–{Math.min(offset + 24, jobs.data.total)} of {jobs.data.total}
                </span>
                <button
                  className={button}
                  disabled={offset + 24 >= jobs.data.total || jobs.isFetching}
                  onClick={() => setOffset(offset + 24)}
                >
                  Next page
                </button>
              </div>
            )}
          </section>
          {(selected || job) && (
            <div ref={detailPanel} className="min-w-0 scroll-mt-20">
              <section className="space-y-4 rounded-lg border p-4">
                {job ? (
                  <>
                    <div className="flex flex-wrap justify-between gap-3">
                      <h2 className="text-xl">
                        {job.recipe.title} · revision {job.revision}
                      </h2>
                      <button
                        className={button}
                        onClick={() => {
                          setSelected('')
                          setParams({ view: 'library' })
                        }}
                      >
                        Close details
                      </button>
                    </div>
                    <p className="break-words">
                      <StudioStatus status={job.status} />{' '}
                      {job.status === 'running' && job.progress && `— ${job.progress}`}
                    </p>
                    {job.creationStage && (
                      <div aria-live="polite" className="rounded-xl border bg-muted/30 p-4 text-sm">
                        <p className="font-medium">{humanize(job.creationStage)}</p>
                        <p className="mt-1 text-muted-foreground">
                          {job.creationStage === 'film_critique'
                            ? 'Rendering is complete. The independent encoded-film critique is running before review becomes available.'
                            : job.creationStage === 'release_review'
                              ? 'Creation and critique finished. Watch this film, approve it, then choose a posting time.'
                              : 'Production state refreshes automatically. No posting occurs during creation.'}
                        </p>
                        {job.creationError && (
                          <p role="alert" className="mt-2">
                            {job.creationError}
                          </p>
                        )}
                      </div>
                    )}
                    {['review_pending', 'failed', 'approved'].includes(job.status) && (
                      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-muted/30 p-4">
                        <div className="text-sm">
                          <p className="font-semibold">
                            {job.status === 'approved'
                              ? 'Film approved · not posted'
                              : 'Next: watch and review this film'}
                          </p>
                          <p className="mt-1 text-muted-foreground">
                            {job.status === 'approved'
                              ? 'Choose the Instagram account, placement and release time in Scheduling. This film approval has not scheduled a post.'
                              : 'Open the review dialog to watch, approve or request changes. No Instagram post will be created.'}
                          </p>
                        </div>
                        {job.status === 'approved' ? (
                          <Link
                            className={button}
                            to={`/app/content-studio?view=schedule&job=${job.id}`}
                          >
                            Schedule publication
                          </Link>
                        ) : (
                          <button
                            className={`${button} bg-primary text-primary-foreground`}
                            onClick={() => {
                              setWatched(false)
                              setReviewOpen(true)
                            }}
                          >
                            Review & approve film
                          </button>
                        )}
                      </div>
                    )}
                    {(job.error || job.dispatch_error) && (
                      <p className="text-sm text-red-600">{job.error || job.dispatch_error}</p>
                    )}
                    {video && !reviewOpen && (
                      <video
                        className="mx-auto max-h-[650px] w-full max-w-sm rounded"
                        src={video}
                        controls
                        playsInline
                        onTimeUpdate={(e) => setTimestamp(e.currentTarget.currentTime)}
                        onError={() => {
                          setVideo('')
                          setMessage(
                            'Preview expired or unavailable. Click Preview to refresh its 10-minute signed link.'
                          )
                        }}
                      />
                    )}
                    <div className="flex flex-wrap gap-2">
                      {job.result?.artifacts?.some((a) => a.role === 'video') && (
                        <>
                          <button
                            className={button}
                            disabled={busy}
                            onClick={() => perform(() => asset('video', true))}
                          >
                            Preview
                          </button>
                          <button className={button} onClick={() => perform(() => asset('video'))}>
                            Download MP4
                          </button>
                          <button
                            className={button}
                            onClick={() => perform(() => asset('caption'))}
                          >
                            Caption
                          </button>
                          <button
                            className={button}
                            onClick={() => perform(() => asset('receipt.json'))}
                          >
                            Validation receipt
                          </button>
                        </>
                      )}
                      {['queued', 'running'].includes(job.status) && (
                        <button
                          className={button}
                          disabled={busy}
                          onClick={() =>
                            perform(async () => {
                              await apiClient.post(`${base}/jobs/${selected}/cancel`)
                            })
                          }
                        >
                          Cancel render
                        </button>
                      )}
                    </div>
                    <details>
                      <summary>Cost and validation</summary>
                      <pre className="overflow-auto whitespace-pre-wrap text-xs">
                        {JSON.stringify(
                          job.result?.costs || {
                            compute: 'pending',
                            externalGeneration: 'disabled',
                          },
                          null,
                          2
                        )}
                      </pre>
                    </details>
                    {download && (
                      <a
                        className="inline-block rounded border p-3 underline"
                        href={download.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Save file: {download.role === 'video' ? 'MP4' : download.role}
                      </a>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {job.result?.artifacts
                        ?.filter((a) => /^story-\d+\.mp4$/.test(a.role))
                        .map((a) => (
                          <button
                            className={button}
                            key={a.role}
                            onClick={() => perform(() => asset(a.role))}
                          >
                            Download {a.role}
                          </button>
                        ))}
                    </div>
                    {['review_pending', 'failed'].includes(job.status) && (
                      <Dialog
                        open={reviewOpen}
                        onOpenChange={(value) => {
                          if (!busy) setReviewOpen(value)
                        }}
                      >
                        <DialogContent className="max-h-[90dvh] max-w-2xl overflow-y-auto font-sans">
                          <DialogHeader>
                            <DialogTitle>Review & approve film</DialogTitle>
                            <DialogDescription>
                              {job.recipe.title} · revision {job.revision}. Approval prepares this
                              film for release; it does not post or schedule it.
                            </DialogDescription>
                          </DialogHeader>
                          {job.result?.artifacts?.some((a) => a.role === 'video') && (
                            <button
                              className={button}
                              disabled={busy}
                              onClick={() => perform(() => asset('video', true))}
                            >
                              {video ? 'Reload film preview' : 'Watch film'}
                            </button>
                          )}
                          {video && (
                            <video
                              className="mx-auto max-h-[35dvh] w-full rounded"
                              src={video}
                              controls
                              playsInline
                              onTimeUpdate={(e) => setTimestamp(e.currentTarget.currentTime)}
                              onError={() => {
                                setVideo('')
                                setWatched(false)
                                setMessage('Film preview unavailable. Reload before approving.')
                              }}
                            />
                          )}
                          {job.status === 'failed' && (
                            <p className="text-sm text-red-700">
                              This render failed. You can request a correction, but cannot approve
                              it.
                            </p>
                          )}
                          <label className="flex items-start gap-2 text-sm">
                            <input
                              type="checkbox"
                              className="mt-1 h-4 w-4"
                              checked={watched}
                              disabled={job.status !== 'review_pending'}
                              onChange={(e) => setWatched(e.target.checked)}
                            />
                            I watched this revision and approve its picture, text and audio.
                          </label>
                          <label className="text-sm font-medium" htmlFor="film-review-finding">
                            Review note · optional for approval
                          </label>
                          <textarea
                            id="film-review-finding"
                            aria-label="Review finding"
                            className="min-h-20 w-full rounded border bg-background p-3"
                            placeholder="Optional for approval. Required when asking for changes."
                            value={finding}
                            onChange={(e) => setFinding(e.target.value)}
                          />
                          <details className="text-sm">
                            <summary className="cursor-pointer">
                              Optional: point to a moment
                            </summary>
                            <label className="block text-sm">
                              Timestamp (seconds)
                              <input
                                type="number"
                                min="0"
                                max={job.recipe.durationSec}
                                step="0.1"
                                className="ml-2 w-24 rounded border bg-background p-2"
                                value={Number(timestamp.toFixed(1))}
                                onChange={(e) => setTimestamp(Number(e.target.value))}
                              />
                            </label>
                          </details>
                          {!finding.trim() && (
                            <p className="text-xs text-muted-foreground">
                              To approve, confirm you watched the film above. To request changes,
                              describe what needs correcting.
                            </p>
                          )}
                          {message && (
                            <p role="status" className="text-sm">
                              {message}
                            </p>
                          )}
                          <div className="flex gap-2">
                            {['approve', 'revise'].map((verdict) => (
                              <button
                                key={verdict}
                                className={button}
                                disabled={
                                  busy ||
                                  (verdict === 'revise' && !finding.trim()) ||
                                  (verdict === 'approve' &&
                                    (job.status !== 'review_pending' || !watched))
                                }
                                onClick={() =>
                                  perform(async () => {
                                    await apiClient.post(`${base}/jobs/${selected}/review`, {
                                      recipeSha256: job.recipe_sha256,
                                      verdict,
                                      evidence: [
                                        {
                                          timeSec: timestamp,
                                          finding:
                                            finding.trim() ||
                                            'I watched this revision and approve its picture, text and audio.',
                                        },
                                      ],
                                    })
                                    setMessage(
                                      verdict === 'approve'
                                        ? 'Film approved. Next: download for manual posting or check Instagram delivery. No post has been scheduled.'
                                        : 'Correction requested. Edit the recipe below and render a new bound revision.'
                                    )
                                    setReviewOpen(false)
                                  })
                                }
                              >
                                {verdict === 'approve' ? 'Approve film' : 'Request correction'}
                              </button>
                            ))}
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                    {job.status === 'revision_requested' && job.input_registry_sha256 && (
                      <p className="text-sm">
                        This fresh film needs a new critique-bound creation through Candidate
                        planning. Its source bindings cannot be changed here.
                      </p>
                    )}
                    {job.status === 'revision_requested' &&
                      !job.input_registry_sha256 &&
                      job.revision < 3 && (
                        <div className="space-y-2">
                          <h3>Correction recipe</h3>
                          <p className="text-sm">
                            Keep registered source bindings and the W5 story policy. The producer
                            validates all effects, source files and camera bounds.
                          </p>
                          <textarea
                            aria-label="Revision recipe JSON"
                            className="h-80 w-full rounded border bg-background p-2 font-mono text-xs"
                            value={recipeText}
                            onChange={(e) => setRecipeText(e.target.value)}
                          />
                          <button
                            className={button}
                            disabled={busy}
                            onClick={() => perform(() => submit(true))}
                          >
                            Render next revision
                          </button>
                        </div>
                      )}
                    <details>
                      <summary>Review history</summary>
                      <pre className="whitespace-pre-wrap text-xs">
                        {JSON.stringify(job.reviews || [], null, 2)}
                      </pre>
                    </details>
                  </>
                ) : (
                  <p>
                    {detail.isLoading
                      ? 'Loading generation details…'
                      : 'Generation details unavailable. Refresh or choose another job.'}
                  </p>
                )}
              </section>
            </div>
          )}
        </>
      )}
      {view === 'schedules' && (
        <section className="space-y-3 rounded-lg border p-4">
          <h2 className="text-xl">Draft production schedule</h2>
          <p className="text-sm">
            This schedules renders from existing world templates, not new autonomous artwork
            selection or social posts. Automatic candidate discovery lives in Candidate planning.
          </p>
          <label className="block text-sm">
            World template
            <select
              aria-label="Schedule template"
              className="mt-1 min-h-11 w-full rounded border bg-background p-2"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
            >
              <option value="">Choose artwork / format</option>
              {catalog.data?.templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title} · {t.format}
                </option>
              ))}
            </select>
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <input
              aria-label="First scheduled run (your local time)"
              type="datetime-local"
              className="min-h-11 max-w-full rounded border bg-background p-2"
              value={firstRun}
              onChange={(e) => setFirstRun(e.target.value)}
            />
            <select
              aria-label="Draft frequency"
              className="min-h-11 rounded border bg-background p-2"
              value={interval}
              onChange={(e) => setInterval(Number(e.target.value))}
            >
              <option value={24}>Daily</option>
              <option value={168}>Weekly</option>
            </select>
            <label className="text-sm">
              <input
                type="checkbox"
                checked={enableSchedule}
                onChange={(e) => setEnableSchedule(e.target.checked)}
              />{' '}
              Enable draft rendering
            </label>
            <button
              className={button}
              disabled={!template || !firstRun || busy}
              onClick={() =>
                perform(async () => {
                  await apiClient.post(`${base}/schedules`, {
                    templateId,
                    firstRunAt: new Date(firstRun).toISOString(),
                    intervalHours: interval,
                    enabled: enableSchedule,
                  })
                  setMessage('Draft schedule saved. Nothing will be posted to Instagram.')
                })
              }
            >
              Save schedule
            </button>
          </div>
          {schedules.data?.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center gap-3 text-sm">
              <span>
                {s.template_id} · {s.enabled ? 'enabled' : 'disabled'} · next{' '}
                {new Date(s.next_run_at).toLocaleString()}
              </span>
              {s.enabled && (
                <button
                  className={button}
                  onClick={() =>
                    perform(async () => {
                      await apiClient.post(`${base}/schedules/${s.id}/disable`)
                    })
                  }
                >
                  Disable
                </button>
              )}
            </div>
          ))}
        </section>
      )}
    </Container>
  )
}
